import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const observeOnce = (element, callback) => {
    if (!element) return;

    if (!('IntersectionObserver' in window)) {
        callback();
        return;
    }

    const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        observer.disconnect();
        callback();
    }, { rootMargin: '0px 0px -25%' });

    observer.observe(element);
};

const Animations = {
    init() {
        const activeLevel = document.querySelector('.mobile-menu__level.is-active');

        if (activeLevel) {
            this.animateMobileMenuLevel(activeLevel);
        }

        this.header();
        this.breadcrumbs();
        this.hero();
        this.accesRapides();
        this.offres();
        this.appBanner();
        this.bentoGrid();
        this.events();
        this.pushActualitesAnimation();
        this.footerAnimation();
        this.contactServices();
        this.contactAgence();
        this.offreHero();
        this.avantages();
        this.documentsList();
        this.jobOffers();
        this.faq();
        this.faqSecondary();
        this.storytelling();
        this.tunnel();
        this.popup();
        this.simulation();
        this.storiesSlider();
        this.testimonialsSlider();
        this.contentPortrait();
        this.fullWidthFaq();
        this.servicesCards();
        this.heroSearchResults();
        this.searchResultsList();
        this.heroSearchDefault();
        this.eventsList();
        this.heroSearchDark();
        this.offersList();
    },

    header() {
        const logo = document.querySelector('.site-header__logo');
        const navItems = document.querySelectorAll('.desktop-nav__item');
        const actions = document.querySelectorAll('.search-btn, .mobile-menu-btn');

        const tl = gsap.timeline();

    if (logo) {
        tl.from(logo, {
            opacity: 0,
            y: 24,
            duration: 0.15,
            ease: 'power1.out',
        });
    }

    if (navItems.length) {
        tl.from(
            navItems,
            {
            opacity: 0,
            y: 24,
            duration: 0.15,
            stagger: 0.05,
            ease: 'power1.out',
            },
            '-=0.05'
        );
    }

    if (actions.length) {
        tl.from(
            actions,
            {
            opacity: 0,
            y: 24,
            duration: 0.15,
            stagger: 0.05,
            ease: 'power1.out',
            },
            '-=0.10'
        );
    }
    },

    animateMobileMenuLevel(level) {
        if (!level) return;

        const title = level.querySelector('h3');
        const back = level.querySelector('.mobile-menu__back');
        const list = level.querySelector('.mobile-menu__list');
        const footerLinks = level.querySelector('.mega-menu__footer-links');
        const buttons = level.querySelector('.buttons-container');
        const assistance = level.querySelector('.mega-menu__assistance');

        const DURATION = 0.2;
        const STEP = '<+0.05';
        const EASE = 'power1.inOut';

        const tl = gsap.timeline();

        // Back button (levels 2+)
        if (back) {
            tl.from(back, {
                opacity: 0,
                y: -24,
                duration: DURATION,
                ease: EASE,
            });
        }

        // Title
        if (title) {
            tl.from(title, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }

        // Navigation items
        if (list) {
            tl.from(list.children, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }

        // Footer links
        if (footerLinks) {
            tl.from(footerLinks, {
                // opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }

        // CTA buttons
        if (buttons) {
            tl.from(buttons, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }

        // Assistance buttons
        if (assistance) {
            tl.from(assistance, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }

        level.dataset.animated = 'true';
    },

    breadcrumbs() {
        const section = document.querySelector('.breadcrumbs');
        if (!section) return;

        const list = section.querySelector('.breadcrumbs__list');
        const items = section.querySelectorAll('.breadcrumbs__item');

        const DURATION = 0.2;
        const EASE = 'power1.inOut';

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: section,
                start: 'top 90%',
                once: true,
            },
        });

        // Breadcrumb list
        if (list) {
            tl.from(list, {
                opacity: 0,
                y: -24,
                duration: DURATION,
                ease: EASE,
            });
        }

        // Breadcrumb items
        if (items.length) {
            tl.from(items, {
                opacity: 0,
                y: -16,
                duration: DURATION,
                ease: EASE,
                stagger: 0.05,
            }, '<+0.05');
        }
    },

    hero() {
        const sections = document.querySelectorAll('.hero');
        if (!sections.length) return;

        sections.forEach((section) => {
            const image = section.querySelector('.hero__image');
            const title = section.querySelector('.hero__title');
            const cta = section.querySelector('.button-white');
            const tl = gsap.timeline();

    if (image) {
        tl.from(image, {
            opacity: 0,
            duration: 0.5,
        });
    }

    if (title) {
        tl.from(
            title,
            {
            opacity: 0,
            y: 32,
            duration: 0.2,
            },
            0.25
        );
    }

    if (cta) {
        tl.from(
            cta,
            {
            opacity: 0,
            y: 32,
            duration: 0.2,
            },
            0.30
        );
    }
        });
    },

    accesRapides() {
        const sections = document.querySelectorAll('.acces-rapides');
        if (!sections.length) return;

        sections.forEach((section) => {
            const title = section.querySelector('.acces-rapides__title');
            const tabs = section.querySelector('.acces-rapides__tabs');
            const cards = section.querySelectorAll(
                '.acces-rapides__panel.is-active .swiper-slide'
            );

            const animatedElements = [title, tabs, ...cards].filter(Boolean);

            // Start the reveal as soon as the page has loaded; do not wait for scroll.
            gsap.set(animatedElements, { opacity: 0, y: 24 });
            section.classList.add('is-animated');

            const tl = gsap.timeline();

                if (title) {
                    tl.fromTo(title, {
                        opacity: 0,
                        y: 24,
                    }, {
                        opacity: 1,
                        y: 0,
                        duration: 0.3,
                        ease: 'power2.out',
                    });
                }

                if (tabs) {
                    tl.fromTo(tabs, {
                        opacity: 0,
                        y: 24,
                    }, {
                        opacity: 1,
                        y: 0,
                        duration: 0.3,
                        ease: 'power2.out',
                    }, '-=0.15');
                }

                if (cards.length) {
                    tl.fromTo(cards, {
                        opacity: 0,
                        y: 24,
                    }, {
                        opacity: 1,
                        y: 0,
                        duration: 0.3,
                        stagger: 0.08,
                        ease: 'power2.out',
                    }, '-=0.10');
                }
        });
    },

    offres() {
        const sections = document.querySelectorAll('.news-section');
        if (!sections.length) return;

        sections.forEach((section) => {
            const label = section.querySelector('.text-label');
            const title = section.querySelector('.news-section__heading');
            const cards = section.querySelectorAll('.card--news');

            observeOnce(section, () => {
                const tl = gsap.timeline();

            if (label) {
                tl.fromTo(label, {
                    opacity: 0,
                    y: 24,
                }, {
                    opacity: 1,
                    y: 0,
                    duration: 0.2,
                    ease: 'power2.out',
                });
            }

    if (title) {
        tl.from(
            title,
            {
            opacity: 0,
            y: 32,
            duration: 0.3,
            ease: 'power2.out',
            },
            '-=0.1'
        );
    }

    if (cards.length) {
        tl.from(cards, {
        opacity: 0,
        duration: 0.3,
        stagger: 0.05,
        ease: 'power2.out',
        });
    }
            });
        });
    },

    appBanner() {
        const sections = document.querySelectorAll('.app-crc-banner');
        if (!sections.length) return;

        const DURATION = 0.2;
        const STEP = '<+0.05';
        const EASE = 'power1.inOut';

        sections.forEach((section) => {
            const label = section.querySelector('.text-label');
            const title = section.querySelector('.title');
            const features = section.querySelectorAll('.app-crc-banner__feature');
            // ✅ Target the desktop right block (direct child of container)
            const images = section.querySelector('.container > .app-crc-banner__right .app-crc-banner__images');
            const qrCode = section.querySelector('.container > .app-crc-banner__right .app-crc-banner__qr-wrapper');
            const apps = section.querySelector('.app-crc-banner__apps');

            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: section,
                    start: 'top 75%',
                    once: true,
                },
            });

            // 1 — Sur-titre
            if (label) {
                tl.from(label, {
                    opacity: 0,
                    y: -24,
                    duration: DURATION,
                    ease: EASE,
                });
            }

            // 2 — Titre de section
            if (title) {
                tl.from(title, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }

            // 3 — Features, all at once
            if (features.length) {
                tl.from(features, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }

            // 4 & 5 — Images block + QR wrapper together
            const imagesAndQr = [images, qrCode].filter(Boolean);
            if (imagesAndQr.length) {
                tl.from(imagesAndQr, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }

            // 6 — CTA store buttons
            if (apps) {
                tl.from(apps, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }
        });

        /* Legacy duplicate animation block retained below for reference.

        // 1 — Sur-titre
        if (label) {
            tl.from(label, {
                opacity: 0,
                y: -24,
                duration: DURATION,
                ease: EASE,
            });
        }

        // 2 — Titre de section
        if (title) {
            tl.from(title, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }

        // 3 — Features, all at once
        if (features.length) {
            tl.from(features, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }

        // 4 & 5 — Images block + QR wrapper together
        tl.from([images, qrCode].filter(Boolean), {
            opacity: 0,
            y: -32,
            duration: DURATION,
            ease: EASE,
        }, STEP);

        // 6 — CTA store buttons
        if (apps) {
            tl.from(apps, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }
        */
    },

    bentoGrid() {
        const sections = document.querySelectorAll('.bento-grid');
        if (!sections.length) return;

        const DURATION = 0.2;        // 200ms everywhere
        const STEP = '<+0.05';       // next step starts 50ms after previous one STARTED
        const EASE = 'power1.inOut'; // closest GSAP match to "ease in and out"

        sections.forEach((section) => {
            const label = section.querySelector('.text-label');
            const title = section.querySelector('.title');
            const description = section.querySelector('.bento-grid__description');
            const benefits = section.querySelectorAll('.bento-grid__benefit');
            const media = section.querySelector('.bento-grid__media');

            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: section,
                    start: 'top 75%',
                    once: true,
                },
            });

            // 1 — Sur-titre AND Bento grid media, simultaneous, first in order
            if (label) {
                tl.from(label, {
                    opacity: 0,
                    y: -24,
                    duration: DURATION,
                    ease: EASE,
                });
            }

            if (media) {
                tl.from(media, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, '<'); // '<' = starts at the same time as the previous tween started
            }

            // 2 — Titre de section
            if (title) {
                tl.from(title, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }

            // 3 — Paragraphe descriptif
            if (description) {
                tl.from(description, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }

            // 4 — Arguments / bénéfices, all together (no individual stagger)
            if (benefits.length) {
                tl.from(benefits, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }
        });
    },

    events() {
        const sections = document.querySelectorAll('.events');
        if (!sections.length) return;

        const DURATION = 0.2;        // 200ms everywhere
        const STEP = '<+0.05';       // next step starts 50ms after previous one STARTED
        const EASE = 'power1.inOut'; // closest GSAP match to "ease in and out"

        sections.forEach((section) => {
            const label = section.querySelector('.text-label');        // ⚠️ add to markup, see above
            const title = section.querySelector('.title');
            const description = section.querySelector('.events__description'); // ⚠️ add to markup, see above
            const cards = section.querySelectorAll('.event-card');
            const buttons = section.querySelectorAll('.button-primary'); // desktop + mobile CTA

            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: section,
                    start: 'top 75%',
                    once: true,
                },
            });

            // 1 — Sur-titre
            if (label) {
                tl.from(label, {
                    opacity: 0,
                    y: -24,
                    duration: DURATION,
                    ease: EASE,
                });
            }

            // 2 — Titre de section
            if (title) {
                tl.from(title, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }

            // 3 — Paragraphe descriptif
            if (description) {
                tl.from(description, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }

            // 4 — Tuiles événement, all together (no individual stagger)
            if (cards.length) {
                tl.from(cards, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }

            // 5 — Bouton (spec note text is duplicated from step 4 — treated as its own
            // sequential step here; flip position to '<' if it should fire with the cards instead)
            if (buttons.length) {
                tl.from(buttons, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }
        });
    },

    pushActualitesAnimation() {
        const sections = document.querySelectorAll('.push-actualites');
        if (!sections.length) return;

        sections.forEach((section) => {
            // Selectors
            const label = section.querySelector('.text-label');
            const title = section.querySelector('.title');
            const subtitle = section.querySelector('.push-actualites__subtitle');
            const cards = section.querySelectorAll('.push-actualites__item');
            const buttonWrap = section.querySelector('.push-actualites__cta-wrap');

            // Create a timeline with default properties shared by all elements
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: section,
                    start: 'top 75%',
                    once: true,
                },
                defaults: {
                    duration: 0.2,        // "200ms"
                    ease: 'power1.inOut', // "Ease in and out"
                    opacity: 0            // "Fade in, opacity 0-100%"
                }
            });

            // 1. SUR-TITRE
            // Appears first (0ms)
            if (label) {
                tl.from(label, {
                    y: 24 // Offset: 24px
                }, 0);
            }

            // 2. TITRE DE SECTION
            // Delay: 50ms from timeline start
            if (title) {
                tl.from(title, {
                    y: 32 // Offset: 32px
                }, 0.05);
            }

            // 3. PARAGRAPHE DESCRIPTIF
            // Delay: 50ms from previous (100ms total)
            if (subtitle) {
                tl.from(subtitle, {
                    y: 32
                }, 0.10);
            }

            // 4. TUILES (ARTICLES)
            // "Ces tuiles apparaissent toutes en même temps" -> We target the NodeList without stagger
            // Delay: 50ms from previous (150ms total)
            if (cards.length > 0) {
                tl.from(cards, {
                    y: 32
                }, 0.15);
            }

            // 5. BOUTON
            // Delay: 50ms from previous (200ms total)
            if (buttonWrap) {
                tl.from(buttonWrap, {
                    y: 32
                }, 0.20);
            }
        });
    },

    footerAnimation() {
        const footer = document.querySelector('.footer');
        if (!footer) return;

        // Selectors
        const title = footer.querySelector('.site-footer__newsletter-title');
        const form = footer.querySelector('.site-footer__newsletter-form');
        
        // We look for a separator. If you use a border instead of an HTML element, 
        // this will be skipped gracefully while preserving the strict 50ms timing for the rest.
        const separator = footer.querySelector('.site-footer__separator, hr'); 
        
        const columns = footer.querySelectorAll('.site-footer__column');
        const copyright = footer.querySelector('.site-footer__copyright');

        // Create a timeline with default properties shared by all elements
        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: footer,
                start: 'top 80%', // Triggers when the top of the footer hits 80% of the viewport height
                once: true,
            },
            defaults: {
                duration: 0.2,        // "200ms"
                ease: 'power1.inOut', // "Ease in and out"
                opacity: 0            // "Fade in, opacity 0-100%"
            }
        });

        // 1 & 2. TITRE DE LA SECTION & CHAMPS NEWSLETTER
        // "Ce champs apparaît en même temps que le titre de la page" (0ms)
        const headerElements = [];
        if (title) headerElements.push(title);
        if (form) headerElements.push(form);

        if (headerElements.length > 0) {
            tl.from(headerElements, { 
                y: 32 // Offset: 32px
            }, 0); // Start at exactly 0ms
        }

        // 3. SÉPARATEUR
        // "Ce séparateur apparaît en deuxième... Delay: 50ms"
        if (separator) {
            tl.from(separator, { 
                y: 32 
            }, 0.05); // Absolute start at 50ms
        }

        // 4. LIENS (Colonnes)
        // "Ces liens apparaissent en troisième... Delay: 50ms"
        if (columns.length > 0) {
            tl.from(columns, { 
                y: 32 
            }, 0.10); // Absolute start at 100ms
        }

        // 5. COPYRIGHT
        // "Ce titre apparaît en quatrième... Delay: 50ms"
        if (copyright) {
            tl.from(copyright, { 
                y: 32 
            }, 0.15); // Absolute start at 150ms
        }
    },

    contactServices() {
        const section = document.querySelector('.contact-services');
        if (!section) return;

        const label = section.querySelector('.text-label');
        const title = section.querySelector('.contact-services__title');
        const tabs = section.querySelector('.contact-services__tabs-wrapper');
        const cards = section.querySelectorAll('.contact-services__card');

        const DURATION = 0.2;
        const STEP = '<+0.05';
        const EASE = 'power1.inOut';

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: section,
                start: 'top 75%',
                once: true,
            },
        });

        // 1 — Sur-titre
        if (label) {
            tl.from(label, {
                opacity: 0,
                y: -24,
                duration: DURATION,
                ease: EASE,
            });
        }

        // 2 — Titre
        if (title) {
            tl.from(title, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }

        // 3 — Tabs
        if (tabs) {
            tl.from(tabs, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }

        // 4 — Cards
        if (cards.length) {
            tl.from(cards, {
                opacity: 0,
                y: 0,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }
    },

    contactAgence() {
        const section = document.querySelector('.contact-agence');
        if (!section) return;

        const label = section.querySelector('.text-label');
        const title = section.querySelector('.contact-agence__title');
        const controls = section.querySelector('.contact-agence__controls');
        const card = section.querySelector('.contact-agence__card.is-active');

        const DURATION = 0.2;
        const STEP = '<+0.05';
        const EASE = 'power1.inOut';

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: section,
                start: 'top 75%',
                once: true,
            },
        });

        // 1 — Sur-titre
        if (label) {
            tl.from(label, {
                opacity: 0,
                y: -24,
                duration: DURATION,
                ease: EASE,
            });
        }

        // 2 — Titre
        if (title) {
            tl.from(title, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }

        // 3 — Dropdown
        if (controls) {
            tl.from(controls, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }

        // 4 — Agency card
        if (card) {
            tl.from(card, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }
    },

    offreHero() {
        const section = document.querySelector('.offre-hero');
        if (!section) return;

        const title = section.querySelector('.offre-hero__content .title');
        const subtitle = section.querySelector('.offre-hero__subtitle');
        const cta = section.querySelector('.button-primary');
        const media = section.querySelector('.offre-hero__media');
        const reassurance = section.querySelectorAll('.reassurance-item');

        const DURATION = 0.2;
        const STEP = '<+0.05';
        const EASE = 'power1.inOut';

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: section,
                start: 'top 75%',
                once: true,
            },
        });

        // 1 — Title
        if (title) {
            tl.from(title, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            });
        }

        // 2 — Description
        if (subtitle) {
            tl.from(subtitle, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }

        // 3 — CTA
        if (cta) {
            tl.from(cta, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }

        // 4 — Image + tag
        if (media) {
            tl.from(media, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }

        // 5 — Reassurance items
        if (reassurance.length) {
            tl.from(reassurance, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }
    },

    avantages() {
        const section = document.querySelector('.avantages');
        if (!section) return;

        const items = section.querySelectorAll('.avantages__item');

        if (!items.length) return;

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            items.forEach((item) => {
                item.style.opacity = '1';
                item.style.transform = 'none';
            });
            return;
        }

        const animateItems = () => {
            gsap.from(items, {
                opacity: 0,
                y: -32,
                duration: 0.2,
                ease: 'power1.inOut',
            });
        };

        if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver((entries, currentObserver) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        animateItems();
                        currentObserver.disconnect();
                    }
                });
            }, { threshold: 0.2 });

            observer.observe(section);
            return;
        }

        animateItems();
    },

    documentsList() {
        const section = document.querySelector('.documents-list');
        if (!section) return;

        const label = section.querySelector('.text-label');
        const title = section.querySelector('.title');
        const description = section.querySelector('.documents-list__description');
        const cards = section.querySelectorAll('.documents-list__card');

        const DURATION = 0.2;
        const STEP = '<+0.05';
        const EASE = 'power1.inOut';

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: section,
                start: 'top 75%',
                once: true,
            },
        });

        // 1 — Sur-titre
        if (label) {
            tl.from(label, {
                opacity: 0,
                y: -24,
                duration: DURATION,
                ease: EASE,
            });
        }

        // 2 — Titre
        if (title) {
            tl.from(title, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }

        // 3 — Description
        if (description) {
            tl.from(description, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }

        // 4 — Documents
        if (cards.length) {
            tl.from(cards, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }
    },

    jobOffers() {
        const section = document.querySelector('.job-offers');
        if (!section) return;

        const label = section.querySelector('.job-offers__label');
        const title = section.querySelector('.title');
        const cta = section.querySelector('.job-offers__cta');
        const cards = section.querySelectorAll('.job-offer-card');

        const DURATION = 0.2;
        const STEP = '<+0.05';
        const EASE = 'power1.inOut';

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: section,
                start: 'top 75%',
                once: true,
            },
        });

        // 1 — Sur-titre
        if (label) {
            tl.from(label, {
                opacity: 0,
                y: -24,
                duration: DURATION,
                ease: EASE,
            });
        }

        // 2 — Titre
        if (title) {
            tl.from(title, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }

        // 3 — CTA
        if (cta) {
            tl.from(cta, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }

        // 4 — Job offers
        if (cards.length) {
            tl.from(cards, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }
    },

    faq() {
        const sections = document.querySelectorAll('.faq--primary');
        if (!sections.length) return;

        const DURATION = 0.2;
        const STEP = '<+0.05';
        const EASE = 'power1.inOut';

        sections.forEach((section) => {
            const label = section.querySelector('.text-label');
            const title = section.querySelector('.faq__title');
            const actions = section.querySelector('.faq__actions');
            const accordions = section.querySelectorAll('.faq__accordion');

            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: section,
                    start: 'top 75%',
                    once: true,
                },
            });

            // 1 — Sur-titre
            if (label) {
                tl.from(label, {
                    opacity: 0,
                    y: -24,
                    duration: DURATION,
                    ease: EASE,
                });
            }

            // 2 — Titre
            if (title) {
                tl.from(title, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }

            // 3 — CTA
            if (actions) {
                tl.from(actions, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }

            // 4 — Accordions
            if (accordions.length) {
                tl.from(accordions, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }
        });
    },

    faqSecondary() {
        const sections = document.querySelectorAll('.faq--secondary');
        if (!sections.length) return;

        const DURATION = 0.2;
        const STEP = '<+0.05';
        const EASE = 'power1.inOut';

        sections.forEach((section) => {
            const label = section.querySelector('.text-label');
            const title = section.querySelector('.faq__title');
            const description = section.querySelector('.faq__description');
            const actions = section.querySelector('.faq__actions');
            const accordions = section.querySelectorAll('.faq__accordion');

            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: section,
                    start: 'top 75%',
                    once: true,
                },
            });

            // 1 — Sur-titre
            if (label) {
                tl.from(label, {
                    opacity: 0,
                    y: -24,
                    duration: DURATION,
                    ease: EASE,
                });
            }

            // 2 — Titre
            if (title) {
                tl.from(title, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }

            // 3 — Description
            if (description) {
                tl.from(description, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }

            // 4 — Buttons
            if (actions) {
                tl.from(actions, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }

            // 5 — Accordions
            if (accordions.length) {
                tl.from(accordions, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }
        });
    },

    storytelling() {
        const sections = document.querySelectorAll('.storytelling');
        if (!sections.length) return;

        const DURATION = 0.2;
        const STEP = '<+0.05';
        const EASE = 'power1.inOut';

        sections.forEach((section) => {
            const media = section.querySelector('.storytelling__media');
            const label = section.querySelector('.text-label');
            const title = section.querySelector('.title');
            const description = section.querySelector('.storytelling__description');
            const actions = section.querySelector('.storytelling__actions');
            const benefits = section.querySelectorAll('.storytelling__benefit, .storytelling__reassurance-item');

            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: section,
                    start: 'top 75%',
                    once: true,
                },
            });

            // 1 — Media (image or video)
            if (media) {
                tl.from(media, {
                    opacity: 0,
                    x: -32,
                    duration: DURATION,
                    ease: EASE,
                });
            }

            // 2 — Sur-titre
            if (label) {
                tl.from(label, {
                    opacity: 0,
                    y: -24,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }

            // 3 — Titre
            if (title) {
                tl.from(title, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }

            // 4 — Description
            if (description) {
                tl.from(description, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }

            // 5 — Buttons
            if (actions) {
                tl.from(actions, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }

            // 6 — Benefits (optional)
            if (benefits.length) {
                tl.from(benefits, {
                    opacity: 0,
                    y: -32,
                    duration: DURATION,
                    ease: EASE,
                }, STEP);
            }
        });
    },

    tunnel() {
        const root = document.querySelector('[data-tunnel]');
        const intro = root?.querySelector('[data-tunnel-intro]');
        const form = root?.querySelector('[data-tunnel-form]');

        if (!root || !intro || !form || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        const introElements = intro.querySelectorAll(
            '.tunnel-intro__kicker, .tunnel-intro__title, .tunnel-intro__image, .tunnel-intro__cta'
        );

        gsap.from(introElements, {
            opacity: 0,
            y: 24,
            duration: 0.35,
            ease: 'power1.out',
            stagger: 0.08,
            clearProps: 'transform,opacity',
        });

        const formObserver = new MutationObserver(() => {
            if (!form.hidden && form.classList.contains('is-visible')) {
                this.tunnelForm(form);
            }
        });

        formObserver.observe(form, { attributes: true, attributeFilter: ['class', 'hidden'] });

        if (window.jQuery) {
            window.jQuery(document).on('gform_page_loaded', () => this.tunnelForm(form));
        }
    },

    tunnelForm(form) {
        if (!form || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        const progress = form.querySelector('.gf_progressbar_wrapper');
        const page = form.querySelector('.gform_page:not([style*="display: none"])')
            ?? form.querySelector('.gform_page');
        const title = page?.querySelector('.gform_page_title, .gform_title');
        const fields = page?.querySelectorAll('.gform_fields > .gfield');
        const footer = page?.querySelector('.gform_page_footer');
        const help = document.querySelector('[data-tunnel-help]');
        const timeline = gsap.timeline({ defaults: { ease: 'power1.out' } });

        if (progress) {
            timeline.fromTo(progress, { opacity: 0, y: -16 }, { opacity: 1, y: 0, duration: 0.25 });
        }

        if (title) {
            timeline.fromTo(title, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.25 }, '<+0.05');
        }

        if (fields?.length) {
            timeline.fromTo(fields, { opacity: 0, y: 20 }, {
                opacity: 1,
                y: 0,
                duration: 0.25,
                stagger: 0.06,
            }, '<+0.05');
        }

        if (footer) {
            timeline.fromTo(footer, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.25 }, '<+0.05');
        }

        if (help) {
            timeline.fromTo(help, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.25 }, '<+0.05');
        }
    },

    popup(popup = document.querySelector('.pop-up.is-open')) {
        if (!popup) return;

        const overlay = popup.querySelector('.pop-up__overlay');
        const dialog = popup.querySelector('.pop-up__dialog');
        const close = popup.querySelector('.pop-up__close');
        const subtitle = popup.querySelector('.pop-up__subtitle');
        const title = popup.querySelector('.pop-up__title');
        const content = popup.querySelector('.pop-up__content');
        const button = popup.querySelector('.pop-up__button');

        const DURATION = 0.2;
        const STEP = '<+0.05';
        const EASE = 'power1.inOut';

        const tl = gsap.timeline();

        // 1 — Overlay
        if (overlay) {
            tl.from(overlay, {
                opacity: 0,
                duration: DURATION,
                ease: EASE,
            });
        }

        // 2 — Dialog
        if (dialog) {
            tl.fromTo(dialog,
                {
                    opacity: 0,
                    scale: 0.96,
                    y: 24,
                },
                {
                    opacity: 1,
                    scale: 1,
                    y: 0,
                    duration: DURATION,
                    ease: EASE,
                },
                STEP
            );
        }

        // 3 — Close button
        if (close) {
            tl.from(close, {
                opacity: 0,
                y: -24,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }

        // 4 — Sur-titre
        if (subtitle) {
            tl.from(subtitle, {
                opacity: 0,
                y: -24,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }

        // 5 — Titre
        if (title) {
            tl.from(title, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }

        // 6 — Content
        if (content) {
            tl.from(content, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }

        // 7 — CTA
        if (button) {
            tl.from(button, {
                opacity: 0,
                y: -32,
                duration: DURATION,
                ease: EASE,
            }, STEP);
        }
    },

    simulation() {
        const sections = document.querySelectorAll('.outils.simulation');

        if (!sections.length) return;

        sections.forEach(section => {
            const header = section.querySelector('.outils__header');
            const nav = section.querySelector('.outils__nav');
            const slider = section.querySelector('.swiper');

            if (!slider) return;

            const animateCard = (card) => {
                if (!card || card.dataset.animated === 'true') return;

                card.dataset.animated = 'true';

                const tl = gsap.timeline();

                const badge = card.querySelector('.outils__card-badge');
                const icon = card.querySelector('.outils__card-icon');
                const intro = card.querySelector('.outils__card-intro');
                const amount = card.querySelector('.outils__card-amount');
                const segments = card.querySelectorAll('.outils__card-segment');
                const legendItems = card.querySelectorAll('.outils__card-legend-item');
                const action = card.querySelector('.outils__card-action');

                if (badge) {
                    tl.from(badge, {
                        scale: 0.6,
                        opacity: 0,
                        duration: 0.35,
                        ease: "back.out(2)"
                    });
                }

                if (icon) {
                    tl.from(icon, {
                        scale: 0,
                        rotate: -25,
                        opacity: 0,
                        duration: 0.4,
                        ease: "back.out(1.8)"
                    }, "-=0.15");
                }

                if (intro || amount) {
                    tl.from([
                        intro,
                        amount
                    ].filter(Boolean), {
                        y: 15,
                        opacity: 0,
                        stagger: 0.08,
                        duration: 0.35
                    }, "-=0.15");
                }

                if (segments.length) {
                    tl.from(segments, {
                        scaleX: 0,
                        transformOrigin: "left center",
                        stagger: 0.08,
                        duration: 0.45,
                        ease: "power2.out"
                    }, "-=0.1");
                }

                if (legendItems.length) {
                    tl.from(legendItems, {
                        x: -15,
                        opacity: 0,
                        stagger: 0.07,
                        duration: 0.3
                    }, "-=0.2");
                }

                if (action) {
                    tl.from(action, {
                        y: 15,
                        opacity: 0,
                        duration: 0.35
                    }, "-=0.1");
                }
            };

            const animateVisibleCards = () => {
                if (!slider) return;

                const visibleCards = slider.querySelectorAll('.swiper-slide-visible .outils__card');
                const cards = visibleCards.length
                    ? visibleCards
                    : slider.querySelectorAll('.outils__card');

                cards.forEach(animateCard);
            };

            const animateSection = () => {
                const targets = [header, slider, nav].filter(Boolean);
                if (!targets.length) {
                    animateVisibleCards();
                    return;
                }

                const tl = gsap.timeline();

                if (header?.children.length) {
                    tl.from(header.children, { y: 30, opacity: 0, stagger: 0.12, duration: 0.55, ease: "power2.out" });
                }
                if (slider) {
                    tl.from(slider, { y: 40, opacity: 0, duration: 0.6 }, tl.duration() ? "-=0.2" : 0);
                }
                if (nav) {
                    tl.from(nav, { y: 20, opacity: 0, duration: 0.4 }, tl.duration() ? "-=0.3" : 0);
                }

                animateVisibleCards();
            };

            // A standalone ScrollTrigger refreshes every active trigger. Some
            // one-time triggers can remove themselves during that refresh,
            // which causes the GSAP internal `.end` error seen on this block.
            if ('IntersectionObserver' in window) {
                const observer = new IntersectionObserver((entries) => {
                    if (!entries.some((entry) => entry.isIntersecting)) return;

                    observer.disconnect();
                    animateSection();
                }, { rootMargin: '0px 0px -25%' });

                observer.observe(section);
            } else {
                animateSection();
            }

            if (slider) {
                requestAnimationFrame(() => {
                    const swiper = slider.swiper;

                    if (!swiper) return;

                    swiper.on('slideChangeTransitionEnd', () => {
                        slider.querySelectorAll('.swiper-slide-visible .outils__card')
                            .forEach(card => {
                                card.dataset.animated = '';
                                animateCard(card);
                            });
                    });
                });
            }
        });
    },

    storiesSlider() {
        const sections = document.querySelectorAll('.stories-slider');

        if (!sections.length) return;

        sections.forEach(section => {
            const header = section.querySelector('.stories-slider__header');
            const nav = section.querySelector('.stories-slider__nav-wrapper');
            const slider = section.querySelector('.stories-slider__swiper');

            if (!slider) return;

            const animateCard = (card) => {
                if (!card || card.dataset.animated === 'true') return;

                card.dataset.animated = 'true';

                const tag = card.querySelector('.stories-slider__tag-wrapper');
                const media = card.querySelector('.stories-slider__video');
                const play = card.querySelector('.stories-slider__play-btn');

                const tl = gsap.timeline();

                if (tag) {
                    tl.from(tag, {
                        y: -10,
                        opacity: 0,
                        duration: 0.25,
                        ease: 'power2.out'
                    });
                }

                if (media) {
                    tl.from(media, {
                        scale: 0.9,
                        opacity: 0,
                        duration: 0.45,
                        ease: 'power2.out'
                    }, '-=0.1');
                }

                if (play) {
                    tl.from(play, {
                        scale: 0,
                        opacity: 0,
                        duration: 0.35,
                        ease: 'back.out(2)'
                    }, '-=0.2');
                }
            };

            const animateSection = () => {
                const tl = gsap.timeline();

                if (header?.children.length) {
                    tl.from(header.children, {
                        y: 30,
                        opacity: 0,
                        stagger: 0.1,
                        duration: 0.4,
                        ease: 'power2.out'
                    });
                }

                tl.from(slider, {
                    y: 30,
                    opacity: 0,
                    duration: 0.45,
                    ease: 'power2.out'
                }, tl.duration() ? '-=0.15' : 0);

                if (nav) {
                    tl.from(nav, {
                        opacity: 0,
                        x: 20,
                        duration: 0.35,
                        ease: 'power2.out'
                    }, tl.duration() ? '-=0.2' : 0);
                }
            };

            if ('IntersectionObserver' in window) {
                const observer = new IntersectionObserver((entries) => {
                    if (!entries.some((entry) => entry.isIntersecting)) return;

                    observer.disconnect();
                    animateSection();
                }, { rootMargin: '0px 0px -25%' });

                observer.observe(section);
            } else {
                animateSection();
            }
        });
    },

    testimonialsSlider() {
        const sections = document.querySelectorAll('.testimonials-slider');

        if (!sections.length) return;

        sections.forEach(section => {
            const header = section.querySelector('.testimonials-slider__header');
            const nav = section.querySelector('.testimonials-slider__nav-wrapper');
            const footer = section.querySelector('.testimonials-slider__footer');
            const slider = section.querySelector('.testimonials-slider__slider');

            if (!slider) return;

            const animateCard = (card) => {
                if (!card || card.dataset.animated === 'true') return;

                card.dataset.animated = 'true';

                const quote = card.querySelector('.testimonials-slider__quote');
                const meta = card.querySelector('.testimonials-slider__meta');
                const tag = card.querySelector('.testimonials-slider__card-tag');

                const tl = gsap.timeline();

                tl.from(card, {
                    y: 40,
                    opacity: 0,
                    duration: 0.45,
                    ease: 'power2.out'
                });

                if (quote) {
                    tl.from(quote, {
                        y: 20,
                        opacity: 0,
                        duration: 0.3
                    }, '-=0.25');
                }

                if (meta) {
                    tl.from(meta, {
                        y: 15,
                        opacity: 0,
                        duration: 0.25
                    }, '-=0.2');
                }

                if (tag) {
                    tl.from(tag, {
                        // scale: 0.85,
                        opacity: 0,
                        duration: 0.25,
                        ease: 'back.out(2)'
                    }, '-=0.15');
                }
            };

            observeOnce(section, () => {

                    const tl = gsap.timeline();

                    if (header && header.children.length) {
                        tl.from(header.children, {
                            y: 30,
                            opacity: 0,
                            stagger: 0.12,
                            duration: 0.45,
                            ease: 'power2.out'
                        });
                    }

                    if (nav) {
                        tl.from(nav, {
                            x: 20,
                            opacity: 0,
                            duration: 0.35,
                            ease: 'power2.out'
                        }, '-=0.2');
                    }

                    if (slider) {
                        tl.from(slider, {
                            y: 30,
                            opacity: 0,
                            duration: 0.45,
                            ease: 'power2.out'
                        }, '-=0.2');
                    }

                    if (footer) {
                        tl.from(footer, {
                            y: 20,
                            opacity: 0,
                            duration: 0.35,
                            ease: 'power2.out'
                        }, '-=0.15');
                    }

                    slider
                        .querySelectorAll('.swiper-slide-visible .testimonials-slider__card')
                        .forEach(animateCard);
                }
            );

            // Swiper may not be initialized yet at this point in the script's
            // execution — defer to next frame so `slider.swiper` is guaranteed to exist.
            requestAnimationFrame(() => {
                const swiper = slider.swiper;

                if (!swiper) return;

                swiper.on('slideChangeTransitionEnd', () => {
                    slider
                        .querySelectorAll('.swiper-slide-visible .testimonials-slider__card')
                        .forEach(card => {
                            card.dataset.animated = '';
                            animateCard(card);
                        });
                });
            });
        });
    },

    contentPortrait() {
        const sections = document.querySelectorAll('.content-portrait');

        if (!sections.length) return;

        sections.forEach(section => {

            const header = section.querySelector('.content-portrait__header');
            const cards = section.querySelectorAll('.content-portrait__grid-item');
            const members = section.querySelectorAll('.content-portrait__member');
            const actions = section.querySelector('.content-portrait__actions');

            observeOnce(section, () => {

                    const tl = gsap.timeline();

                    if (header && header.children.length) {
                        tl.from(header.children, {
                            y: 30,
                            opacity: 0,
                            stagger: 0.12,
                            duration: 0.45,
                            ease: 'power2.out'
                        });
                    }

                    if (cards.length) {
                        tl.from(cards, {
                            y: 40,
                            opacity: 0,
                            scale: 0.95,
                            stagger: 0.12,
                            duration: 0.5,
                            ease: 'power2.out'
                        }, "-=0.2");

                        cards.forEach(card => {
                            const imageWrapper = card.querySelector('.card__image-wrapper');
                            const socials = card.querySelector('.card__socials');
                            const badgeWrapper = card.querySelector('.card__badge-wrapper');
                            const body = card.querySelector('.card__body');

                            if (imageWrapper) {
                                tl.from(imageWrapper, {
                                    scale: 1.1,
                                    opacity: 0,
                                    duration: 0.45,
                                    ease: 'power2.out'
                                }, "<0.05");
                            }

                            if (socials) {
                                tl.from(socials, {
                                    y: -10,
                                    opacity: 0,
                                    duration: 0.25
                                }, "<");
                            }

                            if (badgeWrapper) {
                                tl.from(badgeWrapper, {
                                    x: 20,
                                    opacity: 0,
                                    duration: 0.25
                                }, "<");
                            }

                            if (body) {
                                tl.from(body, {
                                    y: 20,
                                    opacity: 0,
                                    duration: 0.3
                                }, "<");
                            }
                        });
                    }

                    if (actions) {
                        tl.from(actions, {
                            y: 20,
                            opacity: 0,
                            duration: 0.35,
                            ease: 'power2.out'
                        }, "-=0.2");
                    }
                }
            );

            // Animate members when expanding
            const toggle = section.querySelector('[data-content-portrait-toggle]');
            const membersWrapper = section.querySelector('.content-portrait__members');

            if (toggle && membersWrapper) {

                toggle.addEventListener('click', () => {

                    requestAnimationFrame(() => {

                        if (
                            members.length &&
                            (membersWrapper.offsetHeight > 0 ||
                            membersWrapper.classList.contains('is-open'))
                        ) {

                            gsap.from(members, {
                                y: 20,
                                opacity: 0,
                                stagger: 0.05,
                                duration: 0.35,
                                ease: 'power2.out'
                            });

                        }

                    });

                });

            }

        });
    },

    fullWidthFaq() {
        const sections = document.querySelectorAll('.full-width-faq');

        if (!sections.length) return;

        sections.forEach(section => {

            const title = section.querySelector('.full-width-faq__title');
            const accordions = section.querySelectorAll('.full-width-faq__accordion');
            const action = section.querySelector('.full-width-faq__action');

            observeOnce(section, () => {

                    const tl = gsap.timeline();

                    if (title) {
                        tl.from(title, {
                            y: 35,
                            opacity: 0,
                            duration: 0.5,
                            ease: 'power2.out'
                        });
                    }

                    if (accordions.length) {
                        tl.from(accordions, {
                            y: 30,
                            opacity: 0,
                            stagger: 0.12,
                            duration: 0.45,
                            ease: 'power2.out'
                        }, "-=0.2");

                        accordions.forEach(acc => {

                            const trigger = acc.querySelector('.full-width-faq__accordion-trigger');
                            const text = acc.querySelector('.full-width-faq__accordion-title');
                            const icon = acc.querySelector('svg');

                            if (trigger) {
                                tl.from(trigger, {
                                    scale: 0.98,
                                    duration: 0.25
                                }, "<");
                            }

                            if (text) {
                                tl.from(text, {
                                    x: -15,
                                    opacity: 0,
                                    duration: 0.3
                                }, "<");
                            }

                            if (icon) {
                                tl.from(icon, {
                                    rotation: -90,
                                    opacity: 0,
                                    duration: 0.3,
                                    ease: "back.out(2)"
                                }, "<");
                            }

                        });
                    }

                    if (action) {
                        tl.from(action, {
                            y: 25,
                            opacity: 0,
                            duration: 0.4,
                            ease: 'power2.out'
                        }, "-=0.2");
                    }
                }
            );

        });
    },

    servicesCards() {
        const sections = document.querySelectorAll(
            '.services-cards, .secondary-services-cards'
        );

        if (!sections.length) return;

        sections.forEach(section => {

            const label = section.querySelector('.text-label');
            const title = section.querySelector('.title');
            const slides = section.querySelectorAll('.swiper-slide');

            observeOnce(section, () => {

                    const tl = gsap.timeline();

                    if (label) {
                        tl.from(label, {
                            y: 20,
                            opacity: 0,
                            duration: 0.4,
                            ease: 'power2.out'
                        });
                    }

                    if (title) {
                        tl.from(title, {
                            y: 35,
                            opacity: 0,
                            duration: 0.5,
                            ease: 'power2.out'
                        }, "-=0.2");
                    }

                    if (slides.length) {

                        tl.from(slides, {
                            y: 50,
                            opacity: 0,
                            scale: 0.96,
                            stagger: 0.12,
                            duration: 0.6,
                            ease: 'power3.out'
                        }, "-=0.15");

                        slides.forEach(slide => {

                            const tag = slide.querySelector('.card-service__tag-wrapper');
                            const icon = slide.querySelector('.card-service__icon');
                            const cardTitle = slide.querySelector('.card-service__title');
                            const text = slide.querySelector('.card-service__text');
                            const button = slide.querySelector('.card-service__action');

                            if (tag) {
                                tl.from(tag, {
                                    x: -12,
                                    opacity: 0,
                                    duration: 0.25
                                }, "<");
                            }

                            if (icon) {
                                tl.from(icon, {
                                    scale: 0,
                                    rotation: -20,
                                    duration: 0.35,
                                    ease: "back.out(2)"
                                }, "<");
                            }

                            if (cardTitle) {
                                tl.from(cardTitle, {
                                    y: 15,
                                    opacity: 0,
                                    duration: 0.25
                                }, "<");
                            }

                            if (text) {
                                tl.from(text, {
                                    y: 15,
                                    opacity: 0,
                                    duration: 0.25
                                }, "<0.05");
                            }

                            if (button) {
                                tl.from(button, {
                                    y: 12,
                                    opacity: 0,
                                    duration: 0.25
                                }, "<0.05");
                            }

                        });
                    }
                }
            );

        });
    },

    heroSearchResults() {
        const sections = document.querySelectorAll('.hero-search--results');

        if (!sections.length) return;

        sections.forEach(section => {

            const title = section.querySelector('.hero-search__title');
            const results = section.querySelector('.hero-search__results-text');
            const form = section.querySelector('.hero-search__form');

            observeOnce(section, () => {

                    const tl = gsap.timeline();

                    if (title) {
                        tl.from(title, {
                            y: 40,
                            opacity: 0,
                            duration: 0.6,
                            ease: 'power3.out'
                        });
                    }

                    if (results) {
                        tl.from(results, {
                            y: 20,
                            opacity: 0,
                            duration: 0.45,
                            ease: 'power2.out'
                        }, "-=0.3");
                    }

                    if (form) {

                        const inputGroup = form.querySelector('.hero-search__input-group');
                        const button = form.querySelector('button');

                        if (inputGroup) {
                            tl.from(inputGroup, {
                                y: 30,
                                opacity: 0,
                                duration: 0.55,
                                ease: 'power3.out'
                            }, "-=0.2");
                        }

                        if (button) {
                            tl.from(button, {
                                x: 20,
                                opacity: 0,
                                duration: 0.45,
                                ease: 'power2.out'
                            }, "-=0.35");
                        }
                    }
                }
            );

        });
    },

    searchResultsList() {
        const section = document.querySelector('.search-results-list');

        if (!section) return;

        const cards = section.querySelectorAll('.content-card-grid__item');
        const pagination = section.querySelector('.pagination');

        observeOnce(section, () => {

                const tl = gsap.timeline();

                cards.forEach(card => {

                    const media = card.querySelector('.content-card__media');
                    const tag = card.querySelector('.content-card__tag-wrapper');
                    const date = card.querySelector('.content-card__date');
                    const title = card.querySelector('.content-card__title');
                    const description = card.querySelector('.content-card__description');

                    tl.from(card, {
                        y: 40,
                        opacity: 0,
                        duration: 0.45,
                        ease: 'power3.out'
                    });

                    if (media) {
                        tl.from(media, {
                            scale: 0.96,
                            opacity: 0,
                            duration: 0.3
                        }, "<");
                    }

                    if (tag) {
                        tl.from(tag, {
                            x: -12,
                            opacity: 0,
                            duration: 0.25
                        }, "<");
                    }

                    if (date) {
                        tl.from(date, {
                            y: 10,
                            opacity: 0,
                            duration: 0.2
                        }, "<0.05");
                    }

                    if (title) {
                        tl.from(title, {
                            y: 12,
                            opacity: 0,
                            duration: 0.25
                        }, "<");
                    }

                    if (description) {
                        tl.from(description, {
                            y: 10,
                            opacity: 0,
                            duration: 0.2
                        }, "<");
                    }

                });

                if (pagination) {
                    tl.from(pagination, {
                        y: 20,
                        opacity: 0,
                        duration: 0.5,
                        ease: 'power2.out'
                    }, "-=0.1");
                }
            }
        );
    },

    heroSearchDefault() {
        const sections = document.querySelectorAll('.hero-search--default');

        if (!sections.length) return;

        sections.forEach(section => {

            const title = section.querySelector('.hero-search__title');
            const description = section.querySelector('.hero-search__description');
            const filters = section.querySelectorAll('.hero-search__filter');

            observeOnce(section, () => {

                    const tl = gsap.timeline();

                    if (title) {
                        tl.from(title, {
                            y: 40,
                            opacity: 0,
                            duration: 0.5,
                            ease: 'power2.out'
                        });
                    }

                    if (description) {
                        tl.from(description, {
                            y: 25,
                            opacity: 0,
                            duration: 0.4,
                            ease: 'power2.out'
                        }, "-=0.25");
                    }

                    if (filters.length) {
                        tl.from(filters, {
                            y: 30,
                            opacity: 0,
                            stagger: 0.12,
                            duration: 0.45,
                            ease: 'power2.out'
                        }, "-=0.2");
                    }

                }
            );

        });
    },

    eventsList() {
        const section = document.querySelector('.events-list');

        if (!section) return;

        const title = section.querySelector('.events-list__title-wrap');
        const controls = section.querySelector('.events-list__controls');
        const cards = section.querySelectorAll('.events-list__item');

        observeOnce(section, () => {

                const tl = gsap.timeline();

                if (title) {
                    tl.from(title, {
                        y: 30,
                        opacity: 0,
                        duration: 0.5,
                        ease: "power2.out"
                    });
                }

                if (controls) {
                    tl.from(controls, {
                        y: 20,
                        opacity: 0,
                        duration: 0.4,
                        ease: "power2.out"
                    }, "-=0.3");
                }

                cards.forEach(card => {

                    const tags = card.querySelectorAll('.text-label');
                    const eventTitle = card.querySelector('.event-card__title');
                    const calendar = card.querySelector('.event-card__calendar');
                    const details = card.querySelector('.event-card__details');
                    const cta = card.querySelector('.event-card__cta');

                    tl.from(card, {
                        y: 40,
                        opacity: 0,
                        duration: 0.45,
                        ease: "power3.out"
                    });

                    if (tags.length) {
                        tl.from(tags, {
                            x: -10,
                            opacity: 0,
                            stagger: 0.05,
                            duration: 0.2
                        }, "<");
                    }

                    if (eventTitle) {
                        tl.from(eventTitle, {
                            y: 15,
                            opacity: 0,
                            duration: 0.25
                        }, "<");
                    }

                    if (calendar) {
                        tl.from(calendar, {
                            scale: 0.85,
                            opacity: 0,
                            duration: 0.3,
                            ease: "back.out(1.8)"
                        }, "<");
                    }

                    if (details) {
                        tl.from(details, {
                            x: 15,
                            opacity: 0,
                            duration: 0.25
                        }, "<");
                    }

                    if (cta) {
                        tl.from(cta, {
                            scale: 0,
                            rotation: -90,
                            opacity: 0,
                            duration: 0.3,
                            ease: "back.out(2)"
                        }, "<");
                    }

                });

            }
        );
    },

    heroSearchDark() {
        const sections = document.querySelectorAll('.hero-search--dark');

        if (!sections.length) return;

        sections.forEach(section => {

            const label = section.querySelector('.hero-search__label-wrapper');
            const title = section.querySelector('.hero-search__title');
            const filters = section.querySelectorAll('.hero-search__filter');

            observeOnce(section, () => {

                    const tl = gsap.timeline();

                    if (label) {
                        tl.from(label, {
                            y: 20,
                            opacity: 0,
                            duration: 0.35,
                            ease: "power2.out"
                        });
                    }

                    if (title) {
                        tl.from(title, {
                            y: 40,
                            opacity: 0,
                            duration: 0.6,
                            ease: "power3.out"
                        }, "-=0.15");
                    }

                    if (filters.length) {
                        tl.from(filters, {
                            y: 30,
                            opacity: 0,
                            stagger: 0.12,
                            duration: 0.45,
                            ease: "power2.out"
                        }, "-=0.25");
                    }

                }
            );

        });
    },

    offersList() {
        const section = document.querySelector('.offers-list');

        if (!section) return;

        const title = section.querySelector('.offers-list__title-wrap');
        const controls = section.querySelector('.offers-list__controls');
        const cards = section.querySelectorAll('.offers-list__item');

        observeOnce(section, () => {

                const tl = gsap.timeline();

                if (title) {
                    tl.from(title, {
                        y: 30,
                        opacity: 0,
                        duration: 0.5,
                        ease: "power2.out"
                    });
                }

                if (controls) {
                    tl.from(controls, {
                        y: 20,
                        opacity: 0,
                        duration: 0.4,
                        ease: "power2.out"
                    }, "-=0.3");
                }

                cards.forEach(card => {

                    const cardInner = card.querySelector('.job-offer-card');
                    const cardTitle = card.querySelector('.job-offer-card__title');
                    const tags = card.querySelectorAll('.text-label');
                    const date = card.querySelector('.job-offer-card__date');

                    tl.from(cardInner || card, {
                        y: 40,
                        opacity: 0,
                        duration: 0.45,
                        ease: "power3.out"
                    });

                    if (cardTitle) {
                        tl.from(cardTitle, {
                            y: 15,
                            opacity: 0,
                            duration: 0.25
                        }, "<");
                    }

                    if (tags.length) {
                        tl.from(tags, {
                            x: -10,
                            opacity: 0,
                            stagger: 0.05,
                            duration: 0.2
                        }, "<");
                    }

                    if (date) {
                        tl.from(date, {
                            y: 10,
                            opacity: 0,
                            duration: 0.2
                        }, "<");
                    }

                });

            }
        );
    },
};

export default Animations;
